import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  CalendarBlank, 
  CaretLeft, 
  CaretRight, 
  Plus, 
  Sparkle, 
  ArrowsClockwise,
  X,
  Copy,
  Check,
  Clock,
  Trash
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const PLATFORMS = ['Instagram', 'LinkedIn', 'X (Twitter)', 'TikTok', 'Facebook'];
const COLORS = {
  'Instagram': 'bg-pink-500/20 text-pink-400 border-pink-500/20',
  'LinkedIn': 'bg-blue-500/20 text-blue-400 border-blue-500/20',
  'X (Twitter)': 'bg-zinc-500/20 text-zinc-300 border-zinc-500/20',
  'TikTok': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/20',
  'Facebook': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/20',
};

const getWeekDates = (offset = 0) => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff + (offset * 7));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
};

const formatDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const ContentCalendarPage = () => {
  const [viewMode, setViewMode] = useState('month'); // 'week' | 'month'
  const [monthOffset, setMonthOffset] = useState(0);
  const [weekOffset, setWeekOffset] = useState(0);
  const [posts, setPosts] = useState({});
  const [filling, setFilling] = useState(false);
  const [showAdd, setShowAdd] = useState(null);
  const [addForm, setAddForm] = useState({ platform: 'Instagram', time: '10:00', content: '', format: 'Post' });
  const [copied, setCopied] = useState(null);
  const [loadingPosts, setLoadingPosts] = useState(true);

  // Month data
  const now = new Date();
  const monthDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const monthYear = monthDate.getFullYear();
  const monthIdx = monthDate.getMonth();
  const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const daysInMonth = new Date(monthYear, monthIdx + 1, 0).getDate();
  const firstDow = new Date(monthYear, monthIdx, 1).getDay();

  // Week data
  const weekDates = getWeekDates(weekOffset);
  const weekLabel = `${weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const fetchPosts = useCallback(async () => {
    setLoadingPosts(true);
    try {
      let start, end;
      if (viewMode === 'month') {
        start = `${monthYear}-${String(monthIdx + 1).padStart(2, '0')}-01`;
        end = `${monthYear}-${String(monthIdx + 1).padStart(2, '0')}-${daysInMonth}`;
      } else {
        start = formatDate(weekDates[0]);
        end = formatDate(weekDates[6]);
      }
      const { data } = await axios.get(`${API_URL}/api/calendar/posts?start_date=${start}&end_date=${end}`);
      const grouped = {};
      (data.posts || []).forEach(p => {
        if (!grouped[p.date]) grouped[p.date] = [];
        grouped[p.date].push(p);
      });
      setPosts(grouped);
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setLoadingPosts(false);
    }
  }, [viewMode, monthOffset, weekOffset, monthYear, monthIdx, daysInMonth]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const fillMyWeek = async () => {
    setFilling(true);
    try {
      const dates = getWeekDates(weekOffset);
      const startDate = formatDate(dates[0]);
      await axios.post(`${API_URL}/api/calendar/fill-week`, {
        start_date: startDate,
        platforms: PLATFORMS,
      });
      await fetchPosts();
    } catch (err) {
      console.error('Fill week failed:', err);
    } finally {
      setFilling(false);
    }
  };

  const addPost = async (dateKey) => {
    if (!addForm.content.trim()) return;
    try {
      await axios.post(`${API_URL}/api/calendar/posts`, {
        date: dateKey,
        ...addForm
      });
      setShowAdd(null);
      setAddForm({ platform: 'Instagram', time: '10:00', content: '', format: 'Post' });
      await fetchPosts();
    } catch (err) {
      console.error('Failed to add post:', err);
    }
  };

  const removePost = async (dateKey, postId) => {
    try {
      await axios.delete(`${API_URL}/api/calendar/posts/${postId}`);
      setPosts(prev => ({
        ...prev,
        [dateKey]: (prev[dateKey] || []).filter(p => p.id !== postId)
      }));
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  const copyContent = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const isTodayDate = (d) => formatDate(d) === formatDate(new Date());
  const todayStr = formatDate(new Date());

  return (
    <DashboardLayout>
      <div data-testid="content-calendar-page" className="space-y-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[rgba(200,240,96,0.06)] border border-white/[0.07] flex items-center justify-center">
              <CalendarBlank size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]">Content Calendar</h1>
              <p className="text-zinc-400 text-sm">Plan and schedule your content</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
              {['month', 'week'].map(m => (
                <button key={m} onClick={() => setViewMode(m)} data-testid={`view-${m}-btn`}
                  className="px-3 py-1.5 text-xs font-medium capitalize transition-all"
                  style={{ background: viewMode === m ? 'rgba(200,240,96,0.08)' : 'transparent', color: viewMode === m ? '#c8f060' : 'rgba(245,240,232,0.4)' }}>
                  {m}
                </button>
              ))}
            </div>
            <button onClick={fillMyWeek} disabled={filling} data-testid="fill-week-btn"
              className="bg-white text-black hover:bg-zinc-200 transition-all rounded-full font-medium px-6 py-2.5 text-sm disabled:opacity-50 flex items-center gap-2">
              {filling ? <><ArrowsClockwise size={16} className="animate-spin" /> Filling your week…</> : <><Sparkle size={16} /> Fill My Week</>}
            </button>
          </div>
        </motion.div>

        {/* Navigation */}
        <div className="flex items-center justify-between bg-[#141414] border border-white/[0.07] rounded-[14px] px-5 py-3">
          <button onClick={() => viewMode === 'month' ? setMonthOffset(o => o - 1) : setWeekOffset(w => w - 1)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="prev-btn">
            <CaretLeft size={18} />
          </button>
          <div className="text-center">
            <span className="font-medium font-['Instrument_Serif',serif]">{viewMode === 'month' ? monthLabel : weekLabel}</span>
            {(viewMode === 'month' ? monthOffset : weekOffset) !== 0 && (
              <button onClick={() => viewMode === 'month' ? setMonthOffset(0) : setWeekOffset(0)} className="ml-3 text-xs text-zinc-500 hover:text-white transition-colors">Today</button>
            )}
          </div>
          <button onClick={() => viewMode === 'month' ? setMonthOffset(o => o + 1) : setWeekOffset(w => w + 1)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="next-btn">
            <CaretRight size={18} />
          </button>
        </div>

        {/* Calendar Grid */}
        {loadingPosts ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : viewMode === 'month' ? (
          /* ═══ MONTHLY VIEW ═══ */
          <div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {DAYS.map(d => (
                <div key={d} className="text-center text-[10px] font-medium py-1.5" style={{ color: 'rgba(245,240,232,0.3)' }}>{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: firstDow }).map((_, i) => <div key={`e${i}`} className="min-h-[80px]" />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateKey = `${monthYear}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const dayPosts = posts[dateKey] || [];
                const isToday = dateKey === todayStr;

                return (
                  <div key={day}
                    className="min-h-[80px] rounded-xl p-1.5 flex flex-col transition-all cursor-pointer hover:border-white/20"
                    style={{
                      background: isToday ? 'rgba(200,240,96,0.04)' : 'rgba(255,255,255,0.01)',
                      border: `1px solid ${isToday ? 'rgba(200,240,96,0.15)' : 'rgba(255,255,255,0.04)'}`,
                    }}
                    onClick={() => setShowAdd(showAdd === dateKey ? null : dateKey)}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium ${isToday ? 'text-[#c8f060]' : 'text-zinc-500'}`}>{day}</span>
                      {dayPosts.length > 0 && (
                        <span className="w-4 h-4 rounded-full text-[8px] font-bold flex items-center justify-center" style={{ background: 'rgba(200,240,96,0.1)', color: '#c8f060' }}>
                          {dayPosts.length}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 space-y-0.5 overflow-hidden">
                      {dayPosts.slice(0, 2).map(post => (
                        <div key={post.id} className="rounded px-1 py-0.5 text-[8px] truncate" style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(245,240,232,0.5)' }}>
                          {post.platform}: {post.content?.slice(0, 25)}
                        </div>
                      ))}
                      {dayPosts.length > 2 && <div className="text-[8px]" style={{ color: 'rgba(245,240,232,0.25)' }}>+{dayPosts.length - 2} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* ═══ WEEKLY VIEW ═══ */
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {weekDates.map((date, i) => {
              const key = formatDate(date);
              const dayPosts = posts[key] || [];
              const today = isTodayDate(date);

              return (
                <motion.div key={key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className={`bg-zinc-900/50 border rounded-2xl p-3 min-h-[280px] flex flex-col ${today ? 'border-white/30' : 'border-white/5'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-xs text-zinc-500">{DAYS[i]}</span>
                      <p className={`text-lg font-medium font-['Instrument_Serif',serif] ${today ? 'text-white' : 'text-zinc-400'}`}>{date.getDate()}</p>
                    </div>
                    <button onClick={() => setShowAdd(showAdd === key ? null : key)} data-testid={`add-post-${DAYS[i].toLowerCase()}`}
                      className="p-1 hover:bg-white/10 rounded-lg transition-colors text-zinc-500 hover:text-white">
                      <Plus size={14} />
                    </button>
                  </div>

                  <AnimatePresence>
                    {showAdd === key && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        className="mb-2 overflow-hidden">
                        <div className="bg-zinc-800/50 rounded-xl p-2 space-y-2">
                          <select value={addForm.platform} onChange={e => setAddForm(p => ({...p, platform: e.target.value}))}
                            className="w-full bg-zinc-900 border border-white/10 rounded-lg text-white text-xs px-2 py-1.5">
                            {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                          </select>
                          <input type="time" value={addForm.time} onChange={e => setAddForm(p => ({...p, time: e.target.value}))}
                            className="w-full bg-zinc-900 border border-white/10 rounded-lg text-white text-xs px-2 py-1.5" />
                          <textarea value={addForm.content} onChange={e => setAddForm(p => ({...p, content: e.target.value}))}
                            placeholder="Content idea..." rows={2}
                            className="w-full bg-zinc-900 border border-white/10 rounded-lg text-white placeholder:text-zinc-600 text-xs px-2 py-1.5 resize-none" />
                          <div className="flex gap-1">
                            <button onClick={() => addPost(key)} className="flex-1 bg-white text-black text-xs py-1 rounded-lg hover:bg-zinc-200">Add</button>
                            <button onClick={() => setShowAdd(null)} className="px-2 text-zinc-500 hover:text-white"><X size={12} /></button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="flex-1 space-y-1.5 overflow-y-auto">
                    {dayPosts.map(post => (
                      <div key={post.id} className={`rounded-lg p-2 border text-xs ${COLORS[post.platform] || 'bg-zinc-800/50 text-zinc-300 border-zinc-700/30'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium truncate">{post.platform}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => copyContent(post.content, post.id)} className="opacity-50 hover:opacity-100">
                              {copied === post.id ? <Check size={10} /> : <Copy size={10} />}
                            </button>
                            <button onClick={() => removePost(key, post.id)} data-testid={`delete-post-${post.id}`} className="opacity-50 hover:opacity-100">
                              <Trash size={10} />
                            </button>
                          </div>
                        </div>
                        <p className="line-clamp-2 opacity-80">{post.content}</p>
                        <div className="flex items-center justify-between mt-1 opacity-60">
                          <span className="flex items-center gap-1"><Clock size={8} /> {post.time}</span>
                          {post.created_by && <span className="text-[10px]">{post.created_by}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Add Post Modal (for monthly view) */}
        <AnimatePresence>
          {viewMode === 'month' && showAdd && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[200] flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}
              onClick={() => setShowAdd(null)}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }}
                className="w-full max-w-sm mx-4 rounded-2xl p-5"
                style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)' }}
                onClick={e => e.stopPropagation()}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: '#f5f0e8' }}>Add Post — {showAdd}</h3>
                <div className="space-y-2">
                  <select value={addForm.platform} onChange={e => setAddForm(p => ({...p, platform: e.target.value}))}
                    className="w-full bg-zinc-900 border border-white/10 rounded-lg text-white text-xs px-3 py-2">
                    {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input type="time" value={addForm.time} onChange={e => setAddForm(p => ({...p, time: e.target.value}))}
                    className="w-full bg-zinc-900 border border-white/10 rounded-lg text-white text-xs px-3 py-2" />
                  <textarea value={addForm.content} onChange={e => setAddForm(p => ({...p, content: e.target.value}))}
                    placeholder="Content idea..." rows={3}
                    className="w-full bg-zinc-900 border border-white/10 rounded-lg text-white placeholder:text-zinc-600 text-xs px-3 py-2 resize-none" />
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => setShowAdd(null)} className="flex-1 py-2 rounded-lg text-xs" style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.5)' }}>Cancel</button>
                    <button onClick={() => addPost(showAdd)} className="flex-1 py-2 rounded-lg text-xs font-semibold" style={{ background: '#c8f060', color: '#080808' }}>Add Post</button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};

export default ContentCalendarPage;
